/**
 * Speaking to the assistant, and hearing it answer.
 *
 * Recognition runs in the browser, on a Whisper model served by this platform
 * from its own origin. That is a deliberate choice over the Web Speech API,
 * which is one line of code and would have worked today: Chrome implements
 * `SpeechRecognition` by streaming the microphone to Google's servers. On a
 * platform whose whole argument is that the language model runs on the
 * customer's own hardware, shipping a microphone that does not would be a
 * contradiction a buyer is entitled to notice.
 *
 * So: the audio is captured, resampled and transcribed inside the tab, and
 * nothing is uploaded. `allowRemoteModels` is false, so a missing model file
 * disables the microphone rather than quietly fetching one from the internet.
 *
 * Speaking back is the browser's own `speechSynthesis`, which is local on
 * every platform that matters and needs no model of ours.
 */
// Type-only, so nothing of the library is pulled into the bundle by it.
// The library itself is imported where it is first needed; see `transformers`
// below for why.
import type { AutomaticSpeechRecognitionPipeline } from '@huggingface/transformers';
// URLs only: Vite emits these files into the build and nothing is bundled.
import ortAsyncifyMjs from 'onnxruntime-web/ort-wasm-simd-threaded.asyncify.mjs?url';
import ortAsyncifyWasm from 'onnxruntime-web/ort-wasm-simd-threaded.asyncify.wasm?url';
import ortPlainMjs from 'onnxruntime-web/ort-wasm-simd-threaded.mjs?url';
import ortPlainWasm from 'onnxruntime-web/ort-wasm-simd-threaded.wasm?url';

/** The ONNX runtime, served from this origin rather than a CDN. */
const ORT_ASYNCIFY = { mjs: ortAsyncifyMjs, wasm: ortAsyncifyWasm };
const ORT_PLAIN = { mjs: ortPlainMjs, wasm: ortPlainWasm };

/**
 * transformers.js's own rule: Safari before 26 cannot run the asyncify build
 * without WebGPU, so it gets the plain one.
 */
function plainWasmBuild(): boolean {
  const ua = navigator.userAgent;
  const safari = /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|Android/.test(ua);
  const version = Number(/Version\/(\d+)/.exec(ua)?.[1] ?? 0);
  return safari && version > 0 && version < 26 && !navigator.gpu;
}

/**
 * WebGPU, without pulling in @webgpu/types for one property.
 *
 * Only its presence is ever read — whether the pipeline can actually use it
 * is decided by trying, because an adapter that exists and a driver that will
 * run a model are different questions.
 */
declare global {
  interface Navigator {
    readonly gpu?: unknown;
  }
}

/** Whisper's only input rate. Everything is resampled to this. */
const SAMPLE_RATE = 16_000;

/** Longer than any spoken question, and a bound on a stuck recording. */
export const MAX_UTTERANCE_SECONDS = 30;

/**
 * Deciding when somebody has stopped talking.
 *
 * Whisper is not a streaming model — it transcribes a finished clip — so
 * something has to decide when the clip ends. Making the learner press the
 * button again works and is honest, but it puts a second of their reaction
 * time between the end of the sentence and the start of the answer, every
 * time, and it means holding the question in your head while you reach for
 * the mouse.
 *
 * So: end-of-speech is detected. Three numbers do it, and each is a trade
 * between cutting somebody off and making them wait.
 *
 * `SILENCE_TO_END` is the one that matters. Too short and a mid-sentence
 * breath ends the question; too long and the pause before the answer feels
 * like the thing has hung. Nine hundred milliseconds is longer than the gap
 * inside a sentence and shorter than the gap people leave when they have
 * finished.
 *
 * The threshold is measured rather than fixed, because the right value in a
 * quiet room and in an open-plan office differ by more than any constant can
 * cover: the noise floor is sampled before the learner starts, and speech is
 * anything well above it.
 */
const NOISE_SAMPLE_MS = 300;
/** Speech is this many times the measured noise floor, and never below this. */
const SPEECH_OVER_NOISE = 3.5;
const MIN_SPEECH_LEVEL = 0.012;
/** How much silence ends the question, once something has been said. */
const SILENCE_TO_END_MS = 900;
/** Below this, nothing was said; a cough should not ask a question. */
const MIN_SPEECH_MS = 350;
/** Nobody speaking at all, so the microphone does not stay open. */
const GIVE_UP_WITHOUT_SPEECH_MS = 8_000;

/**
 * Served by this app, never fetched from a CDN.
 *
 * `scripts/fetch-whisper-model.mjs` puts the files under public/models, so the
 * browser loads them from the same origin as the page and an air-gapped
 * install works unchanged.
 */
/**
 * The library, loaded the first time it is actually wanted.
 *
 * Imported statically this added 570KB — 169KB over the wire — to the main
 * bundle, which every visitor downloads whether or not they ever press the
 * microphone. Measured: 380KB before the voice feature, 949KB after. For a
 * control most sessions never touch, that is the wrong trade, and it is the
 * sort of cost that does not announce itself.
 *
 * Dynamic, it becomes its own chunk, fetched when the assistant panel opens
 * and warms the recogniser. The settings below belong with it rather than at
 * module scope for the same reason: reading them would have pulled the
 * library in.
 */
let transformers: Promise<typeof import('@huggingface/transformers')> | null = null;

function loadLibrary(): Promise<typeof import('@huggingface/transformers')> {
  if (!transformers) {
    transformers = import('@huggingface/transformers').then((mod) => {
      // Served by this app, never fetched from a CDN.
      //
      // `scripts/fetch-whisper-model.mjs` puts the files under public/models,
      // so the browser loads them from the same origin as the page and an
      // air-gapped install works unchanged.
      mod.env.allowRemoteModels = false;
      mod.env.allowLocalModels = true;
      mod.env.localModelPath = '/models/';
      // The same goes for the ONNX runtime itself. Left unset, transformers.js
      // points it at cdn.jsdelivr.net, so the microphone silently depended on
      // the public internet even with the model served from here. The build
      // copies both runtime variants next to the app; this picks the one the
      // library would have picked from the CDN.
      const onnx = mod.env.backends.onnx;
      if (onnx.wasm) onnx.wasm.wasmPaths = plainWasmBuild() ? ORT_PLAIN : ORT_ASYNCIFY;
      return mod;
    });
  }
  return transformers;
}

/**
 * Which Whisper to use, and why this one.
 *
 * Measured in this browser on the development machine, on five seconds of
 * audio, with the WASM backend running single-threaded (a page that is not
 * cross-origin isolated has no SharedArrayBuffer, so ONNX cannot use threads):
 *
 *     tiny.en   1.5s to load, 2.4s to transcribe
 *     base.en   2.4s to load, 4.7s to transcribe
 *
 * `base.en` despite being twice as slow. The questions this transcribes are
 * full of terms like "insecure direct object references" and "idempotency
 * key", which is exactly where the smaller model gives up, and a mis-heard
 * question costs a whole ten-second answer cycle rather than two seconds of
 * waiting. The transcription is also not the part the learner is waiting on:
 * the answer behind it takes five to ten seconds regardless.
 *
 * Overridable per device, for a machine where that trade lands differently.
 * It takes effect on the next page load: ONNX will not open a second model in
 * a session that already has one.
 */
const MODEL_KEY = 'sx.voice.model';
const DEFAULT_MODEL = 'whisper-base.en';

function chosenModel(): string {
  try {
    const stored = window.localStorage.getItem(MODEL_KEY);
    // Only the two that are fetched; anything else would 404 into an ONNX
    // parse error that reads like a corrupt download.
    if (stored === 'whisper-tiny.en' || stored === 'whisper-base.en') return stored;
  } catch {
    /* private mode */
  }
  return DEFAULT_MODEL;
}

export type SpeechState = 'idle' | 'loading' | 'listening' | 'thinking';

let recogniser: Promise<AutomaticSpeechRecognitionPipeline> | null = null;
let unavailable = false;

/**
 * Is a microphone possible at all here?
 *
 * Three things have to be true, and all three fail quietly rather than
 * throwing, so they are checked rather than attempted: a secure context (
 * `getUserMedia` is undefined on plain http, except on localhost), a device,
 * and the model files. The button is hidden when this is false rather than
 * shown and then failing, because a dead microphone button is worse than no
 * microphone button.
 */
export function speechSupported(): boolean {
  if (unavailable) return false;
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  if (typeof window.AudioContext === 'undefined') return false;
  return true;
}

/**
 * Load the model, once per tab.
 *
 * Roughly 76 MB, so the first call takes a few seconds and every later one is
 * instant — the browser caches it like any other asset. Started on the first
 * press of the microphone rather than at page load, because most visits never
 * use it and the catalogue should not pay for a feature nobody opened.
 */
let warmed = false;

export async function warmUpSpeech(): Promise<void> {
  if (!speechSupported() || warmed) return;
  warmed = true;
  try {
    const transcribe = await loadRecogniser();
    // Reading the weights is only half of it. The first transcription of a
    // session compiles the shaders — twelve seconds of it, on WebGPU — and
    // every one after is under two. Paying that here, against a second of
    // silence while the learner is still deciding what to ask, is the whole
    // point of warming up; without it the first spoken question is the slow
    // one, which is exactly the question a learner judges the feature on.
    await transcribe(new Float32Array(SAMPLE_RATE));
  } catch {
    warmed = false; // so a later press can try again
    /* reported by the caller that actually transcribes */
  }
}

/**
 * Which backend, and in which precision.
 *
 * Measured in Chrome on the development machine, three seconds of audio,
 * steady state after warm-up:
 *
 *     WASM, q8                            4.3s
 *     WebGPU, fp16 encoder + q8 decoder   1.8s
 *
 * The dtype pairing is not a preference, it is the only combination that
 * works. WebGPU cannot open the 8-bit quantised encoder at all, and the fp16
 * *decoder* published alongside it fails graph initialisation — so the
 * encoder is taken in half precision and the decoder in eight-bit. Trying to
 * be consistent about it, in either direction, gets a session that will not
 * open.
 *
 * WASM stays the fallback rather than the other way round, because WebGPU is
 * absent on older browsers, disabled in some managed Chrome policies, and
 * unavailable in the Browser pane this was tested from. A microphone that
 * works everywhere and is quick in most places beats one that is quick and
 * sometimes missing.
 */
async function buildPipeline(model: string): Promise<AutomaticSpeechRecognitionPipeline> {
  const { pipeline } = await loadLibrary();

  if (typeof navigator !== 'undefined' && navigator.gpu) {
    try {
      return (await pipeline('automatic-speech-recognition', model, {
        device: 'webgpu',
        dtype: { encoder_model: 'fp16', decoder_model_merged: 'q8' },
      })) as AutomaticSpeechRecognitionPipeline;
    } catch {
      // No adapter, no fp16 weights fetched, or a driver that will not have
      // it. Not worth reporting: the fallback below is a working microphone.
      usingWebGpu = false;
    }
  }
  return (await pipeline('automatic-speech-recognition', model, {
    dtype: 'q8',
  })) as AutomaticSpeechRecognitionPipeline;
}

/** Reported by `speechBackend()`, for the setup documentation to be checkable. */
let usingWebGpu = typeof navigator !== 'undefined' && !!navigator.gpu;

export function speechBackend(): 'webgpu' | 'wasm' {
  return usingWebGpu ? 'webgpu' : 'wasm';
}

function loadRecogniser(): Promise<AutomaticSpeechRecognitionPipeline> {
  if (!recogniser) {
    recogniser = buildPipeline(chosenModel()).catch((error) => {
      // A failed load must not be remembered as a pending promise, or every
      // later press waits on a rejection that will never settle differently.
      recogniser = null;
      unavailable = true;
      throw error;
    }) as Promise<AutomaticSpeechRecognitionPipeline>;
  }
  return recogniser;
}

export interface RecordingOptions {
  /**
   * Called once, when the speaker has stopped for long enough that the
   * question is over. The caller stops the recording; this never stops it
   * itself, so there is exactly one place a recording ends.
   */
  onSpeechEnd?: () => void;
  /** Called when nobody said anything at all, so the UI can give up quietly. */
  onNothingHeard?: () => void;
}

export interface Recording {
  /** Stop, transcribe, and resolve with what was said. Empty if silence. */
  stop: () => Promise<string>;
  /** Stop and discard. Nothing is transcribed. */
  cancel: () => void;
  /**
   * Loudness right now, 0 to 1, for the level meter.
   *
   * Read rather than pushed, so the component animates on its own frames and
   * a slow render cannot queue up a backlog of level events.
   */
  level: () => number;
}

/**
 * Start recording from the microphone.
 *
 * Returns once permission has been granted and audio is arriving, so the
 * caller can show "listening" at a moment when it is true. The browser's own
 * permission prompt is the only one shown; this never asks for anything else.
 */
export async function startRecording(options: RecordingOptions = {}): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  const context = new AudioContext({ sampleRate: SAMPLE_RATE });
  const source = context.createMediaStreamSource(stream);

  // ScriptProcessor rather than an AudioWorklet: a worklet needs its own
  // module file served separately, and this path is about to be replaced by
  // the resampled buffer anyway. The deprecation is real but the replacement
  // costs a build-time asset for no behavioural gain at this size.
  const processor = context.createScriptProcessor(4096, 1, 1);
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;

  const chunks: Float32Array[] = [];
  let samples = 0;
  let stopped = false;

  // --- end-of-speech detection ------------------------------------------
  // All of it driven off the same audio chunks that are being recorded, so
  // there is no second timer to drift out of step with what was captured.
  let noiseFloor = 0;
  let noiseChunks = 0;
  let speechMs = 0;
  let silenceMs = 0;
  let ended = false;

  const msPerChunk = (4096 / SAMPLE_RATE) * 1000;
  const noiseChunksNeeded = Math.max(1, Math.round(NOISE_SAMPLE_MS / msPerChunk));

  processor.onaudioprocess = (event) => {
    if (stopped) return;
    const input = event.inputBuffer.getChannelData(0);
    // Copied: the event's buffer is reused by the audio thread.
    chunks.push(new Float32Array(input));
    samples += input.length;
    if (samples >= SAMPLE_RATE * MAX_UTTERANCE_SECONDS) {
      stopped = true;
      if (!ended) { ended = true; options.onSpeechEnd?.(); }
      return;
    }

    let sum = 0;
    for (const sample of input) sum += sample * sample;
    const rms = Math.sqrt(sum / input.length);

    // The first fraction of a second is the room, not the speaker. Nothing is
    // judged against a floor that has not been measured yet.
    if (noiseChunks < noiseChunksNeeded) {
      noiseFloor += rms;
      noiseChunks += 1;
      if (noiseChunks === noiseChunksNeeded) noiseFloor /= noiseChunksNeeded;
      return;
    }

    const speaking = rms > Math.max(MIN_SPEECH_LEVEL, noiseFloor * SPEECH_OVER_NOISE);
    if (speaking) {
      speechMs += msPerChunk;
      silenceMs = 0;
    } else {
      silenceMs += msPerChunk;
    }

    if (ended) return;

    if (speechMs >= MIN_SPEECH_MS && silenceMs >= SILENCE_TO_END_MS) {
      // Said something, then stopped. That is the question.
      ended = true;
      options.onSpeechEnd?.();
    } else if (speechMs < MIN_SPEECH_MS && silenceMs >= GIVE_UP_WITHOUT_SPEECH_MS) {
      // The button was pressed and nobody spoke. Closing the microphone is
      // better manners than holding it open until the thirty-second cap.
      ended = true;
      options.onNothingHeard?.();
    }
  };

  source.connect(analyser);
  source.connect(processor);
  // Connected to the destination with no gain: some browsers do not run a
  // ScriptProcessor that reaches nothing, and routing the microphone to the
  // speakers audibly would be an echo.
  const mute = context.createGain();
  mute.gain.value = 0;
  processor.connect(mute);
  mute.connect(context.destination);

  const meter = new Uint8Array(analyser.frequencyBinCount);

  const teardown = () => {
    stopped = true;
    processor.onaudioprocess = null;
    try { processor.disconnect(); } catch { /* already gone */ }
    try { source.disconnect(); } catch { /* already gone */ }
    try { mute.disconnect(); } catch { /* already gone */ }
    for (const track of stream.getTracks()) track.stop();
    void context.close().catch(() => undefined);
  };

  return {
    level: () => {
      if (stopped) return 0;
      analyser.getByteTimeDomainData(meter);
      let peak = 0;
      for (const value of meter) peak = Math.max(peak, Math.abs(value - 128));
      // 128 is silence and 127 of swing is full scale; squashed a little so
      // ordinary speech moves the meter rather than only a shout.
      return Math.min(1, (peak / 128) * 2.5);
    },

    cancel: teardown,

    stop: async () => {
      teardown();
      if (samples < SAMPLE_RATE * 0.25) return '';

      const audio = new Float32Array(samples);
      let offset = 0;
      for (const chunk of chunks) {
        audio.set(chunk, offset);
        offset += chunk.length;
      }

      // Silence in, silence out: Whisper will cheerfully invent a sentence
      // from room tone, and "thank you" appearing in the box because somebody
      // tapped the microphone by accident is worse than nothing happening.
      let energy = 0;
      for (const sample of audio) energy += sample * sample;
      if (Math.sqrt(energy / audio.length) < 0.005) return '';

      const transcribe = await loadRecogniser();
      // No `language` or `task`: this is an English-only checkpoint, and it
      // rejects both rather than ignoring them. Passing them is the obvious
      // thing to write and fails at the first transcription, not at load.
      const result = await transcribe(audio);
      const text = Array.isArray(result)
        ? result.map((part) => part.text).join(' ')
        : (result?.text ?? '');

      return cleanTranscript(text);
    },
  };
}

/**
 * Tidy what Whisper returns.
 *
 * It emits a leading space, and on silence or noise it emits one of a small
 * set of stock phrases — the artefacts of its training data, not something
 * anybody said. Those are dropped rather than shown, because a question box
 * that fills itself with "Thanks for watching!" reads as the product being
 * broken.
 */
function cleanTranscript(raw: string): string {
  const text = raw.replace(/\s+/g, ' ').trim();
  const hallucinations = [
    'thank you.',
    'thanks for watching!',
    'thanks for watching.',
    'you',
    '[blank_audio]',
    '[ silence ]',
    'bye.',
  ];
  if (hallucinations.includes(text.toLowerCase())) return '';
  return text;
}

// ----------------------------------------------------------------- speaking

/**
 * Whether the assistant reads its answers aloud. Remembered per device, like
 * the welcome voice and the theme.
 */
export const SPOKEN_REPLIES_KEY = 'sx.voice.replies';

export function spokenRepliesOn(): boolean {
  try {
    // On by default, by request: the assistant is presented as one you can
    // talk to and hear. The switch in the panel turns it off, per device.
    return window.localStorage.getItem(SPOKEN_REPLIES_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSpokenReplies(on: boolean): void {
  try {
    window.localStorage.setItem(SPOKEN_REPLIES_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode: the choice just will not survive a reload */
  }
}

export function speechOutputSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Read an answer aloud.
 *
 * Citation markers are stripped first: "[1]" is a reference a reader's eye
 * skips and a voice cannot, and hearing "bracket one" after every sentence is
 * enough to make anyone turn the feature off.
 */
export function speak(text: string): void {
  if (!speechOutputSupported()) return;
  stopSpeaking();

  const spoken = text
    .replace(/\[\d{1,2}\]/g, '')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spoken) return;

  const utterance = new SpeechSynthesisUtterance(spoken);
  utterance.rate = 1.02;
  utterance.pitch = 1;

  // A local voice where the browser has one, so nothing is synthesised over
  // the network. Chrome marks its cloud voices `localService: false`.
  const voices = window.speechSynthesis.getVoices();
  const preferred =
    voices.find((v) => v.localService && /^en[-_]?(GB|IN|US)/i.test(v.lang)) ??
    voices.find((v) => v.localService && v.lang.startsWith('en')) ??
    voices.find((v) => v.lang.startsWith('en'));
  if (preferred) utterance.voice = preferred;

  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking(): void {
  if (!speechOutputSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* nothing was speaking */
  }
}
